import { validateActionFlowTopologyContract as validateHeadlessActionFlowTopologyContract } from '@cogo/headless-core/actionflow-studio-readiness'
import type {
  AppSpecFull,
  StudioActionFlowCompilerParamBinding,
  StudioActionFlowEffectBoundary,
  StudioActionFlowEffectBoundaryKind,
  StudioActionFlowPrimitive,
  StudioActionFlowPrimitiveKind,
  StudioActionFlowStateNode,
  StudioActionFlowStateNodeKind,
  StudioActionFlowStateRelation,
  StudioActionFlowStateRelationKind,
  StudioActionFlowStateScope,
  StudioActionFlowTopologyContract,
  StudioActionFlowValidationIssue,
  StudioActionFlowValidationProof,
} from '@/services/block-api-service'

interface BuildActionFlowTopologyOptions {
  source?: string
}

const FORM_OR_TABLE_BLOCK_TYPES = new Set([
  'data-table',
  'form',
  'filter-bar',
  'search',
  'input',
  'select',
  'grid',
])

const MUTATION_WORDS =
  /\b(add|append|create|update|delete|remove|save|submit|approve|reject|mark|fulfill|sync)\b/i
const NAVIGATION_WORDS = /\b(navigate|route|link|tab|page|screen)\b/i
const AUTH_WORDS = /\b(admin|auth|permission|role|rbac|guard|login|sign in)\b/i
const UI_FEEDBACK_WORDS =
  /\b(notification|toast|modal|popup|dialog|alert|feedback)\b/i
const REALTIME_WORDS =
  /\b(realtime|real-time|subscription|subscribe|chat|message|live updates?)\b/i

const KNOWN_ACTIONFLOW_PRIMITIVE_KINDS: StudioActionFlowPrimitiveKind[] = [
  'set-local-state',
  'promote-local-to-global',
  'hydrate-local-from-global',
  'fetch-datasource-to-global',
  'write-global-to-datasource',
  'compute-projection',
  'navigate',
  'guard-by-permission',
  'trigger-ui-feedback',
  'subscribe-realtime-source',
]

function isActionFlowPrimitiveKind(
  value: string,
): value is StudioActionFlowPrimitiveKind {
  return KNOWN_ACTIONFLOW_PRIMITIVE_KINDS.includes(
    value as StudioActionFlowPrimitiveKind,
  )
}

function stableId(value: string | undefined, fallback: string): string {
  const input = (value ?? fallback).trim().toLowerCase()
  const slug = input
    .replace(/[^a-z0-9:/._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || fallback
}

function appText(appSpec: AppSpecFull): string {
  return [
    appSpec.name,
    appSpec.description,
    ...(appSpec.blocks ?? []).flatMap((block) => [
      block.name,
      block.type,
      block.description,
      ...(block.binds ?? []),
    ]),
  ]
    .filter(Boolean)
    .join(' ')
}

function hasActionFlowNeed(appSpec: AppSpecFull): boolean {
  return (
    appSpec.interactionContract?.actionFlowRequired === true ||
    appSpec.blocks.some((block) => (block.binds ?? []).length > 0)
  )
}

function normalizeDatasourceSpecs(
  appSpec: AppSpecFull,
): NonNullable<AppSpecFull['datasources']> {
  return appSpec.datasources ?? []
}

function actionFlowOntologyPlan(appSpec: AppSpecFull) {
  return appSpec.ontologyRetrievalTrace?.actionFlowOntologyPlan
}

function actionFlowOntologyPrimitiveKinds(
  appSpec: AppSpecFull,
): StudioActionFlowPrimitiveKind[] {
  return (actionFlowOntologyPlan(appSpec)?.coverage.primitiveKinds ?? [])
    .filter(isActionFlowPrimitiveKind)
}

function actionFlowOntologyEffectBoundaryKinds(
  appSpec: AppSpecFull,
): Set<StudioActionFlowEffectBoundaryKind> {
  return new Set(
    (actionFlowOntologyPlan(appSpec)?.coverage.effectBoundaryKinds ?? [])
      .filter((kind): kind is StudioActionFlowEffectBoundaryKind =>
        [
          'pure',
          'state',
          'network',
          'navigation',
          'ui',
          'auth',
          'storage',
          'external',
          'device',
          'media',
          'ai',
          'crypto',
          'realtime',
        ].includes(kind as StudioActionFlowEffectBoundaryKind),
      ),
  )
}

function stateNodeKind(
  scope: StudioActionFlowStateScope,
): StudioActionFlowStateNodeKind {
  switch (scope) {
    case 'local':
      return 'local-state'
    case 'appData':
      return 'global-state'
    case 'computed':
      return 'computed-projection'
    case 'datasource':
      return 'datasource'
    case 'route':
      return 'route-context'
    case 'env':
      return 'environment'
    case 'props':
      return 'props'
    case '_results':
      return 'result'
  }
}

function nodeId(
  scope: StudioActionFlowStateScope,
  path: string,
  ownerBlockName?: string,
): string {
  const owner =
    scope === 'local' && ownerBlockName
      ? `${stableId(ownerBlockName, 'block')}:`
      : ''
  return `${scope}:${owner}${stableId(path, scope)}`
}

function extractPrefixedPaths(text: string, prefix: string): string[] {
  const pattern = new RegExp(
    `\\b${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.([a-zA-Z0-9_.[\\]-]+)`,
    'g',
  )
  return [...text.matchAll(pattern)].map((match) => `${prefix}.${match[1]}`)
}

function blockNeedsLocalState(block: AppSpecFull['blocks'][number]): boolean {
  const text = [block.type, block.description, ...(block.binds ?? [])].join(' ')
  return (
    FORM_OR_TABLE_BLOCK_TYPES.has(block.type) ||
    /\b(input|filter|search|select|row|cell|toggle|tab|quantity|draft|selected)\b/i.test(
      text,
    )
  )
}

function inferLocalPaths(block: AppSpecFull['blocks'][number]): string[] {
  const text = [block.description, ...(block.binds ?? [])].join(' ')
  const explicit = extractPrefixedPaths(text, 'local')
  if (explicit.length > 0) return explicit
  if (
    /data-table|table|row|record/i.test(
      [block.type, block.description].join(' '),
    )
  ) {
    return ['local.selectedRecord', 'local.tableDraft']
  }
  if (/search|filter/i.test([block.type, block.description].join(' '))) {
    return ['local.query']
  }
  if (/form|input|select/i.test([block.type, block.description].join(' '))) {
    return ['local.formDraft']
  }
  if (/tab|navigation/i.test([block.type, block.description].join(' '))) {
    return ['local.activeTab']
  }
  return blockNeedsLocalState(block) ? ['local.value'] : []
}

function inferGlobalPaths(appSpec: AppSpecFull): string[] {
  const stateKeys = Object.keys(appSpec.globalState ?? {})
  const globalPaths =
    stateKeys.length > 0
      ? stateKeys.map((key) =>
          key === 'appData' ? 'appData.root' : `appData.${key}`,
        )
      : ['appData.root']
  const explicit = appSpec.blocks.flatMap((block) =>
    extractPrefixedPaths(
      [block.description, ...(block.binds ?? [])].join(' '),
      'appData',
    ),
  )
  return [...new Set([...globalPaths, ...explicit])]
}

function inferComputedPaths(appSpec: AppSpecFull): string[] {
  const explicit = appSpec.blocks.flatMap((block) =>
    extractPrefixedPaths(
      [block.description, ...(block.binds ?? [])].join(' '),
      'computed',
    ),
  )
  if (explicit.length > 0) return [...new Set(explicit)]
  if (appSpec.interactionContract?.requireComputedProjection) {
    return ['computed.appSummary']
  }
  return []
}

function createNode(
  scope: StudioActionFlowStateScope,
  path: string,
  data: Partial<StudioActionFlowStateNode> = {},
): StudioActionFlowStateNode {
  return {
    id: data.id ?? nodeId(scope, path, data.ownerBlockName),
    kind: data.kind ?? stateNodeKind(scope),
    scope,
    path,
    writable:
      data.writable ??
      (scope === 'local' || scope === 'appData' || scope === '_results'),
    ontologyRefs: data.ontologyRefs ?? [`cogo:${stateNodeKind(scope)}`],
    evidenceRefs: data.evidenceRefs ?? [],
    ...(data.ownerBlockName ? { ownerBlockName: data.ownerBlockName } : {}),
    ...(data.datasourceName ? { datasourceName: data.datasourceName } : {}),
  }
}

function expressionForNode(node: StudioActionFlowStateNode): string {
  return `#{${node.path}}`
}

function resultKeyForPrimitiveKind(
  kind: StudioActionFlowPrimitiveKind,
  discriminator?: string,
): string {
  const base = stableId(kind, 'primitive').replace(/-/g, '')
  const suffix = discriminator ? stableId(discriminator, 'source').replace(/-/g, '') : ''
  return `${base}${suffix ? `${suffix.slice(0, 24)}` : ''}Result`
}

function compilerParamBinding(
  role: StudioActionFlowCompilerParamBinding['role'],
  param: string,
  data: Omit<StudioActionFlowCompilerParamBinding, 'role' | 'param'>,
): StudioActionFlowCompilerParamBinding {
  return {
    role,
    param,
    evidenceRefs: data.evidenceRefs,
    ...(data.sourceNodeId ? { sourceNodeId: data.sourceNodeId } : {}),
    ...(data.targetNodeId ? { targetNodeId: data.targetNodeId } : {}),
    ...(data.expression ? { expression: data.expression } : {}),
    ...(data.resultKey ? { resultKey: data.resultKey } : {}),
    ...('value' in data ? { value: data.value } : {}),
  }
}

export function buildActionFlowTopologyContract(
  appSpec: AppSpecFull,
  options: BuildActionFlowTopologyOptions = {},
): StudioActionFlowTopologyContract {
  const source = options.source ?? 'studio-actionflow-topology'
  const nodes = new Map<string, StudioActionFlowStateNode>()
  const boundaries = new Map<string, StudioActionFlowEffectBoundary>()
  const primitives = new Map<string, StudioActionFlowPrimitive>()
  const relations = new Map<string, StudioActionFlowStateRelation>()

  const addNode = (
    node: StudioActionFlowStateNode,
  ): StudioActionFlowStateNode => {
    if (!nodes.has(node.id)) nodes.set(node.id, node)
    return nodes.get(node.id)!
  }
  const addBoundary = (
    id: string,
    kind: StudioActionFlowEffectBoundaryKind,
    sideEffect: boolean,
    evidenceRefs: string[],
    requiredCapability?: string,
  ): string => {
    if (!boundaries.has(id)) {
      boundaries.set(id, {
        id,
        kind,
        sideEffect,
        evidenceRefs,
        ...(requiredCapability ? { requiredCapability } : {}),
      })
    }
    return id
  }
  const addPrimitive = (
    primitive: StudioActionFlowPrimitive,
  ): StudioActionFlowPrimitive => {
    if (!primitives.has(primitive.id)) primitives.set(primitive.id, primitive)
    return primitives.get(primitive.id)!
  }
  const addRelation = (
    kind: StudioActionFlowStateRelationKind,
    sourceNodeId: string,
    targetNodeId: string,
    primitiveRef: string,
    evidenceRefs: string[],
    effectBoundaryRef?: string,
  ) => {
    const id = `${kind}:${sourceNodeId}->${targetNodeId}`
    if (!relations.has(id)) {
      relations.set(id, {
        id,
        kind,
        sourceNodeId,
        targetNodeId,
        primitiveRef,
        ...(effectBoundaryRef ? { effectBoundaryRef } : {}),
        evidenceRefs,
      })
    }
  }

  const plannerPrimitiveKinds = actionFlowOntologyPrimitiveKinds(appSpec)
  const plannerEffectBoundaryKinds =
    actionFlowOntologyEffectBoundaryKinds(appSpec)
  const plannerEvidenceRefs = actionFlowOntologyPlan(appSpec)
    ? ['ontologyRetrievalTrace.actionFlowOntologyPlan']
    : ['appSpec.interactionContract']

  const stateBoundaryRef = addBoundary('boundary:state', 'state', false, [
    'appSpec.interactionContract',
  ])
  const networkBoundaryRef =
    normalizeDatasourceSpecs(appSpec).length > 0
      ? addBoundary(
          'boundary:network',
          'network',
          true,
          ['appSpec.datasources'],
          'network',
        )
      : null
  const navigationBoundaryRef = NAVIGATION_WORDS.test(appText(appSpec))
    ? addBoundary(
        'boundary:navigation',
        'navigation',
        true,
        ['appSpec.pages'],
        'navigation',
      )
    : null
  const authBoundaryRef = AUTH_WORDS.test(appText(appSpec))
    ? addBoundary(
        'boundary:auth',
        'auth',
        true,
        ['prompt', 'appSpec.description'],
        'auth',
      )
    : null

  const globalNodes = inferGlobalPaths(appSpec).map((path) =>
    addNode(
      createNode('appData', path, {
        evidenceRefs: ['appSpec.globalState'],
        ontologyRefs: ['cogo:GlobalState', 'cogo:AppData'],
      }),
    ),
  )
  const primaryGlobalNode =
    globalNodes[0] ??
    addNode(
      createNode('appData', 'appData', {
        evidenceRefs: ['appSpec.globalState'],
        ontologyRefs: ['cogo:GlobalState', 'cogo:AppData'],
      }),
    )

  const localNodes: StudioActionFlowStateNode[] = []
  for (const block of appSpec.blocks) {
    const paths = inferLocalPaths(block)
    for (const path of paths) {
      const local = addNode(
        createNode('local', path, {
          ownerBlockName: block.name,
          evidenceRefs: [`appSpec.blocks.${block.name}.binds`],
          ontologyRefs: ['cogo:LocalState', 'cogo:BlockStateBoundary'],
        }),
      )
      localNodes.push(local)
    }
  }

  const computedNodes = inferComputedPaths(appSpec).map((path) =>
    addNode(
      createNode('computed', path, {
        writable: false,
        evidenceRefs: ['appSpec.interactionContract.requireComputedProjection'],
        ontologyRefs: ['cogo:ComputedProjection', 'cogo:ExpressionPrefix'],
      }),
    ),
  )

  const datasourceNodes = normalizeDatasourceSpecs(appSpec).map(
    (datasource) => {
      const datasourceNode = addNode(
        createNode('datasource', datasource.name, {
          writable: true,
          datasourceName: datasource.name,
          evidenceRefs: [`appSpec.datasources.${datasource.name}`],
          ontologyRefs: ['cogo:Datasource', 'cogo:EffectBoundary'],
        }),
      )
      const cacheNode = addNode(
        createNode('appData', `appData.${datasource.name}`, {
          evidenceRefs: [
            `appSpec.datasources.${datasource.name}`,
            'appSpec.globalState',
          ],
          ontologyRefs: ['cogo:GlobalState', 'cogo:DatasourceCache'],
        }),
      )
      if (networkBoundaryRef) {
        const primitive = addPrimitive({
          id: `primitive:fetch:${stableId(datasource.name, 'datasource')}`,
          kind: 'fetch-datasource-to-global',
          label: `Fetch ${datasource.name} into appData`,
          required: true,
          inputNodeIds: [datasourceNode.id],
          outputNodeIds: [cacheNode.id],
          effectBoundaryRefs: [networkBoundaryRef],
          compilerTargetAction: 'restApi',
          compilerParamBindings: [
            compilerParamBinding('source', 'datasourceName', {
              sourceNodeId: datasourceNode.id,
              value: datasource.name,
              evidenceRefs: [`appSpec.datasources.${datasource.name}`],
            }),
            compilerParamBinding('target', 'target', {
              targetNodeId: cacheNode.id,
              value: cacheNode.path,
              evidenceRefs: ['appSpec.globalState'],
            }),
            compilerParamBinding('result', 'resultTarget', {
              resultKey: resultKeyForPrimitiveKind(
                'fetch-datasource-to-global',
                datasource.name,
              ),
              evidenceRefs: [`appSpec.datasources.${datasource.name}`],
            }),
            compilerParamBinding('adapter-option', 'method', {
              value: 'GET',
              evidenceRefs: [`appSpec.datasources.${datasource.name}`],
            }),
          ],
          ontologyRefs: [
            'cogo:FetchDatasourcePrimitive',
            'cogo:DatasourceRecipe',
          ],
          evidenceRefs: [`appSpec.datasources.${datasource.name}`],
        })
        addRelation(
          'cache',
          datasourceNode.id,
          cacheNode.id,
          primitive.id,
          primitive.evidenceRefs,
          networkBoundaryRef,
        )
      }
      return { datasourceNode, cacheNode }
    },
  )

  const hasPrimitiveKind = (kind: StudioActionFlowPrimitiveKind): boolean =>
    [...primitives.values()].some((primitive) => primitive.kind === kind)

  const getOrCreateBoundary = (
    kind: StudioActionFlowEffectBoundaryKind,
    sideEffect = kind !== 'state' && kind !== 'pure',
  ): string =>
    addBoundary(
      `boundary:${kind}`,
      kind,
      sideEffect,
      plannerEvidenceRefs,
      sideEffect ? kind : undefined,
    )

  const getOrCreateLocalNode = (): StudioActionFlowStateNode => {
    const existing = localNodes[0]
    if (existing) return existing
    const ownerBlockName = appSpec.blocks[0]?.name ?? 'app'
    const node = addNode(
      createNode('local', 'local.value', {
        ownerBlockName,
        evidenceRefs: plannerEvidenceRefs,
        ontologyRefs: ['cogo:LocalState', 'cogo:BlockStateBoundary'],
      }),
    )
    if (!localNodes.some((item) => item.id === node.id)) localNodes.push(node)
    return node
  }

  const getOrCreateComputedNode = (): StudioActionFlowStateNode => {
    const existing = computedNodes[0]
    if (existing) return existing
    const node = addNode(
      createNode('computed', 'computed.appSummary', {
        writable: false,
        evidenceRefs: plannerEvidenceRefs,
        ontologyRefs: ['cogo:ComputedProjection', 'cogo:ExpressionPrefix'],
      }),
    )
    if (!computedNodes.some((item) => item.id === node.id)) {
      computedNodes.push(node)
    }
    return node
  }

  const getOrCreateDatasourcePair = (
    boundaryKind: StudioActionFlowEffectBoundaryKind = 'network',
    options: { datasourceName?: string; forceNew?: boolean } = {},
  ): { datasourceNode: StudioActionFlowStateNode; cacheNode: StudioActionFlowStateNode } => {
    const existing = options.datasourceName
      ? datasourceNodes.find(
          (item) => item.datasourceNode.datasourceName === options.datasourceName,
        )
      : datasourceNodes[0]
    if (existing && !options.forceNew) return existing
    const datasourceName =
      options.datasourceName ??
      (boundaryKind === 'storage'
        ? 'localStorage'
        : boundaryKind === 'external'
          ? 'externalAdapter'
          : boundaryKind === 'realtime'
            ? 'realtimeMessages'
            : 'plannerDatasource')
    const datasourceNode = addNode(
      createNode('datasource', datasourceName, {
        writable: true,
        datasourceName,
        evidenceRefs: plannerEvidenceRefs,
        ontologyRefs: ['cogo:Datasource', 'cogo:EffectBoundary'],
      }),
    )
    const cacheNode = addNode(
      createNode('appData', `appData.${datasourceName}`, {
        evidenceRefs: plannerEvidenceRefs,
        ontologyRefs: ['cogo:GlobalState', 'cogo:DatasourceCache'],
      }),
    )
    const pair = { datasourceNode, cacheNode }
    datasourceNodes.push(pair)
    return pair
  }

  const writeBoundaryKind = (): StudioActionFlowEffectBoundaryKind => {
    if (plannerEffectBoundaryKinds.has('storage')) return 'storage'
    if (plannerEffectBoundaryKinds.has('external')) return 'external'
    return 'network'
  }

  for (const local of localNodes) {
    const primitive = addPrimitive({
      id: `primitive:set-local:${stableId(local.ownerBlockName, 'block')}`,
      kind: 'set-local-state',
      label: `Update ${local.ownerBlockName ?? 'block'} local state`,
      required: true,
      inputNodeIds: [local.id],
      outputNodeIds: [local.id],
      effectBoundaryRefs: [stateBoundaryRef],
      compilerTargetAction: 'setState',
      compilerParamBindings: [
        compilerParamBinding('target', 'target', {
          targetNodeId: local.id,
          value: local.path.replace(/^local\./, ''),
          evidenceRefs: local.evidenceRefs,
        }),
        compilerParamBinding('value', 'value', {
          targetNodeId: local.id,
          expression: '#{event.value}',
          evidenceRefs: local.evidenceRefs,
        }),
      ],
      ontologyRefs: ['cogo:SetLocalStatePrimitive', 'cogo:LocalState'],
      evidenceRefs: local.evidenceRefs,
    })
    addRelation(
      'write',
      local.id,
      local.id,
      primitive.id,
      local.evidenceRefs,
      stateBoundaryRef,
    )
  }

  const needsPromotion =
    localNodes.length > 0 &&
    (appSpec.interactionContract?.requiredStateScopes.includes('appData') ===
      true ||
      appSpec.blocks.some((block) =>
        [block.description, ...(block.binds ?? [])]
          .join(' ')
          .includes('appData.'),
      ))
  if (needsPromotion) {
    for (const local of localNodes.slice(
      0,
      Math.max(1, Math.min(localNodes.length, 4)),
    )) {
      const primitive = addPrimitive({
        id: `primitive:promote:${stableId(local.ownerBlockName, 'block')}`,
        kind: 'promote-local-to-global',
        label: `Promote ${local.ownerBlockName ?? 'block'} local state to appData`,
        required: true,
        inputNodeIds: [local.id],
        outputNodeIds: [primaryGlobalNode.id],
        effectBoundaryRefs: [stateBoundaryRef],
        compilerTargetAction: 'setState',
        compilerParamBindings: [
          compilerParamBinding('source', 'value', {
            sourceNodeId: local.id,
            expression: expressionForNode(local),
            evidenceRefs: local.evidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: primaryGlobalNode.id,
            value: primaryGlobalNode.path,
            evidenceRefs: ['appSpec.globalState'],
          }),
          compilerParamBinding('value', 'value', {
            sourceNodeId: local.id,
            targetNodeId: primaryGlobalNode.id,
            expression: expressionForNode(local),
            evidenceRefs: [
              ...local.evidenceRefs,
              'appSpec.interactionContract.requiredStateScopes',
            ],
          }),
        ],
        ontologyRefs: [
          'cogo:PromoteLocalToGlobalPrimitive',
          'cogo:StateOwnership',
        ],
        evidenceRefs: [
          ...local.evidenceRefs,
          'appSpec.interactionContract.requiredStateScopes',
        ],
      })
      addRelation(
        'promote',
        local.id,
        primaryGlobalNode.id,
        primitive.id,
        primitive.evidenceRefs,
        stateBoundaryRef,
      )
    }
  }

  if (computedNodes.length > 0) {
    const inputNode = datasourceNodes[0]?.cacheNode ?? primaryGlobalNode
    for (const computed of computedNodes) {
      const primitive = addPrimitive({
        id: `primitive:compute:${stableId(computed.path, 'projection')}`,
        kind: 'compute-projection',
        label: `Compute ${computed.path}`,
        required: true,
        inputNodeIds: [inputNode.id],
        outputNodeIds: [computed.id],
        effectBoundaryRefs: [],
        compilerTargetAction: 'compute',
        compilerParamBindings: [
          compilerParamBinding('source', 'expression', {
            sourceNodeId: inputNode.id,
            expression: expressionForNode(inputNode),
            evidenceRefs: inputNode.evidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: computed.id,
            value: computed.path,
            evidenceRefs: computed.evidenceRefs,
          }),
          compilerParamBinding('value', 'expression', {
            sourceNodeId: inputNode.id,
            targetNodeId: computed.id,
            expression: expressionForNode(inputNode),
            evidenceRefs: [...inputNode.evidenceRefs, ...computed.evidenceRefs],
          }),
        ],
        ontologyRefs: [
          'cogo:ComputeProjectionPrimitive',
          'cogo:ExpressionPrefix',
        ],
        evidenceRefs: computed.evidenceRefs,
      })
      addRelation(
        'derive',
        inputNode.id,
        computed.id,
        primitive.id,
        primitive.evidenceRefs,
      )
    }
  }

  if (
    networkBoundaryRef &&
    datasourceNodes.length > 0 &&
    MUTATION_WORDS.test(appText(appSpec))
  ) {
    const target = datasourceNodes[0]
    const primitive = addPrimitive({
      id: `primitive:write-datasource:${stableId(target.datasourceNode.datasourceName, 'datasource')}`,
      kind: 'write-global-to-datasource',
      label: `Persist appData changes to ${target.datasourceNode.datasourceName}`,
      required: true,
      inputNodeIds: [primaryGlobalNode.id],
      outputNodeIds: [target.datasourceNode.id],
      effectBoundaryRefs: [networkBoundaryRef],
      compilerTargetAction: 'restApi',
      compilerParamBindings: [
        compilerParamBinding('source', 'datasourceName', {
          sourceNodeId: primaryGlobalNode.id,
          value: target.datasourceNode.datasourceName,
          evidenceRefs: ['appSpec.globalState'],
        }),
        compilerParamBinding('payload', 'body', {
          sourceNodeId: primaryGlobalNode.id,
          expression: expressionForNode(primaryGlobalNode),
          evidenceRefs: ['appSpec.blocks.binds'],
        }),
        compilerParamBinding('result', 'resultTarget', {
          resultKey: resultKeyForPrimitiveKind(
            'write-global-to-datasource',
            target.datasourceNode.datasourceName,
          ),
          evidenceRefs: [
            `appSpec.datasources.${target.datasourceNode.datasourceName}`,
          ],
        }),
        compilerParamBinding('adapter-option', 'method', {
          value: 'POST',
          evidenceRefs: [
            `appSpec.datasources.${target.datasourceNode.datasourceName}`,
          ],
        }),
      ],
      ontologyRefs: ['cogo:WriteDatasourcePrimitive', 'cogo:EffectBoundary'],
      evidenceRefs: [
        'appSpec.blocks.binds',
        `appSpec.datasources.${target.datasourceNode.datasourceName}`,
      ],
    })
    addRelation(
      'sync',
      primaryGlobalNode.id,
      target.datasourceNode.id,
      primitive.id,
      primitive.evidenceRefs,
      networkBoundaryRef,
    )
  }

  if (navigationBoundaryRef) {
    const route = addNode(
      createNode('route', 'route.path', {
        writable: false,
        evidenceRefs: ['appSpec.pages'],
        ontologyRefs: ['cogo:RouteContext', 'cogo:NavigationGuard'],
      }),
    )
    const primitive = addPrimitive({
      id: 'primitive:navigate',
      kind: 'navigate',
      label: 'Navigate between application routes',
      required: true,
      inputNodeIds: [route.id],
      outputNodeIds: [route.id],
      effectBoundaryRefs: [navigationBoundaryRef],
      compilerTargetAction: 'navigate',
      compilerParamBindings: [
        compilerParamBinding('route', 'to', {
          sourceNodeId: route.id,
          expression: expressionForNode(route),
          evidenceRefs: ['appSpec.pages'],
        }),
        compilerParamBinding('payload', 'params', {
          sourceNodeId: route.id,
          expression: '#{route.params}',
          evidenceRefs: ['appSpec.pages'],
        }),
      ],
      ontologyRefs: ['cogo:NavigationPrimitive', 'cogo:RouteGraph'],
      evidenceRefs: ['appSpec.pages'],
    })
    addRelation(
      'read',
      route.id,
      route.id,
      primitive.id,
      primitive.evidenceRefs,
      navigationBoundaryRef,
    )
  }

  if (authBoundaryRef) {
    const permissions = addNode(
      createNode('appData', 'appData.permissions', {
        evidenceRefs: ['prompt', 'appSpec.description'],
        ontologyRefs: ['cogo:PermissionState', 'cogo:RoleGuard'],
      }),
    )
    const primitive = addPrimitive({
      id: 'primitive:guard-by-permission',
      kind: 'guard-by-permission',
      label: 'Guard privileged actions by permission state',
      required: true,
      inputNodeIds: [permissions.id],
      outputNodeIds: [permissions.id],
      effectBoundaryRefs: [authBoundaryRef],
      compilerTargetStepType: 'conditional',
      compilerParamBindings: [
        compilerParamBinding('condition', 'condition', {
          sourceNodeId: permissions.id,
          expression: expressionForNode(permissions),
          evidenceRefs: ['prompt', 'appSpec.description'],
        }),
        compilerParamBinding('capability', 'requiredCapability', {
          value: 'auth',
          evidenceRefs: ['prompt', 'appSpec.description'],
        }),
      ],
      ontologyRefs: ['cogo:PermissionGuardPrimitive', 'cogo:EffectBoundary'],
      evidenceRefs: ['prompt', 'appSpec.description'],
    })
    addRelation(
      'guard',
      permissions.id,
      permissions.id,
      primitive.id,
      primitive.evidenceRefs,
      authBoundaryRef,
    )
  }

  const ensurePlannerPrimitiveKind = (
    kind: StudioActionFlowPrimitiveKind,
  ): void => {
    if (hasPrimitiveKind(kind)) return

    if (kind === 'set-local-state') {
      const local = getOrCreateLocalNode()
      const primitive = addPrimitive({
        id: `primitive:set-local:${stableId(local.ownerBlockName, 'block')}`,
        kind,
        label: `Update ${local.ownerBlockName ?? 'block'} local state`,
        required: true,
        inputNodeIds: [local.id],
        outputNodeIds: [local.id],
        effectBoundaryRefs: [stateBoundaryRef],
        compilerTargetAction: 'setState',
        compilerParamBindings: [
          compilerParamBinding('target', 'target', {
            targetNodeId: local.id,
            value: local.path.replace(/^local\./, ''),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('value', 'value', {
            targetNodeId: local.id,
            expression: '#{event.value}',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: ['cogo:SetLocalStatePrimitive', 'cogo:LocalState'],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'write',
        local.id,
        local.id,
        primitive.id,
        primitive.evidenceRefs,
        stateBoundaryRef,
      )
      return
    }

    if (kind === 'promote-local-to-global') {
      const local = getOrCreateLocalNode()
      const primitive = addPrimitive({
        id: `primitive:promote:${stableId(local.ownerBlockName, 'block')}`,
        kind,
        label: `Promote ${local.ownerBlockName ?? 'block'} local state to appData`,
        required: true,
        inputNodeIds: [local.id],
        outputNodeIds: [primaryGlobalNode.id],
        effectBoundaryRefs: [stateBoundaryRef],
        compilerTargetAction: 'setState',
        compilerParamBindings: [
          compilerParamBinding('source', 'value', {
            sourceNodeId: local.id,
            expression: expressionForNode(local),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: primaryGlobalNode.id,
            value: primaryGlobalNode.path,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('value', 'value', {
            sourceNodeId: local.id,
            targetNodeId: primaryGlobalNode.id,
            expression: expressionForNode(local),
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: [
          'cogo:PromoteLocalToGlobalPrimitive',
          'cogo:StateOwnership',
        ],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'promote',
        local.id,
        primaryGlobalNode.id,
        primitive.id,
        primitive.evidenceRefs,
        stateBoundaryRef,
      )
      return
    }

    if (kind === 'hydrate-local-from-global') {
      const local = getOrCreateLocalNode()
      const primitive = addPrimitive({
        id: `primitive:hydrate:${stableId(local.ownerBlockName, 'block')}`,
        kind,
        label: `Hydrate ${local.ownerBlockName ?? 'block'} local state from appData`,
        required: true,
        inputNodeIds: [primaryGlobalNode.id],
        outputNodeIds: [local.id],
        effectBoundaryRefs: [stateBoundaryRef],
        compilerTargetAction: 'setState',
        compilerParamBindings: [
          compilerParamBinding('source', 'value', {
            sourceNodeId: primaryGlobalNode.id,
            expression: expressionForNode(primaryGlobalNode),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: local.id,
            value: local.path.replace(/^local\./, ''),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('value', 'value', {
            sourceNodeId: primaryGlobalNode.id,
            targetNodeId: local.id,
            expression: expressionForNode(primaryGlobalNode),
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: [
          'cogo:HydrateLocalFromGlobalPrimitive',
          'cogo:StateOwnership',
        ],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'hydrate',
        primaryGlobalNode.id,
        local.id,
        primitive.id,
        primitive.evidenceRefs,
        stateBoundaryRef,
      )
      return
    }

    if (kind === 'fetch-datasource-to-global') {
      const pair = getOrCreateDatasourcePair('network', {
        datasourceName: plannerPrimitiveKinds.includes(
          'subscribe-realtime-source',
        )
          ? 'plannerDatasource'
          : undefined,
      })
      const boundaryRef = networkBoundaryRef ?? getOrCreateBoundary('network')
      const primitive = addPrimitive({
        id: `primitive:fetch:${stableId(pair.datasourceNode.datasourceName, 'datasource')}`,
        kind,
        label: `Fetch ${pair.datasourceNode.datasourceName ?? 'datasource'} into appData`,
        required: true,
        inputNodeIds: [pair.datasourceNode.id],
        outputNodeIds: [pair.cacheNode.id],
        effectBoundaryRefs: [boundaryRef],
        compilerTargetAction: 'restApi',
        compilerParamBindings: [
          compilerParamBinding('source', 'datasourceName', {
            sourceNodeId: pair.datasourceNode.id,
            value: pair.datasourceNode.datasourceName,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: pair.cacheNode.id,
            value: pair.cacheNode.path,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('result', 'resultTarget', {
            resultKey: resultKeyForPrimitiveKind(kind, pair.datasourceNode.datasourceName),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('adapter-option', 'method', {
            value: 'GET',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: [
          'cogo:FetchDatasourcePrimitive',
          'cogo:DatasourceRecipe',
        ],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'cache',
        pair.datasourceNode.id,
        pair.cacheNode.id,
        primitive.id,
        primitive.evidenceRefs,
        boundaryRef,
      )
      return
    }

    if (kind === 'write-global-to-datasource') {
      const boundaryKind = writeBoundaryKind()
      const pair = getOrCreateDatasourcePair(boundaryKind)
      const boundaryRef =
        boundaryKind === 'network' && networkBoundaryRef
          ? networkBoundaryRef
          : getOrCreateBoundary(boundaryKind)
      const primitive = addPrimitive({
        id: `primitive:write-datasource:${stableId(pair.datasourceNode.datasourceName, 'datasource')}`,
        kind,
        label: `Persist appData changes to ${pair.datasourceNode.datasourceName ?? 'datasource'}`,
        required: true,
        inputNodeIds: [primaryGlobalNode.id],
        outputNodeIds: [pair.datasourceNode.id],
        effectBoundaryRefs: [boundaryRef],
        compilerTargetAction: 'restApi',
        compilerParamBindings: [
          compilerParamBinding('source', 'datasourceName', {
            sourceNodeId: primaryGlobalNode.id,
            value: pair.datasourceNode.datasourceName,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('payload', 'body', {
            sourceNodeId: primaryGlobalNode.id,
            expression: expressionForNode(primaryGlobalNode),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('result', 'resultTarget', {
            resultKey: resultKeyForPrimitiveKind(kind, pair.datasourceNode.datasourceName),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('adapter-option', 'method', {
            value: boundaryKind === 'storage' ? 'PUT' : 'POST',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: ['cogo:WriteDatasourcePrimitive', 'cogo:EffectBoundary'],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'sync',
        primaryGlobalNode.id,
        pair.datasourceNode.id,
        primitive.id,
        primitive.evidenceRefs,
        boundaryRef,
      )
      return
    }

    if (kind === 'compute-projection') {
      const computed = getOrCreateComputedNode()
      const primitive = addPrimitive({
        id: `primitive:compute:${stableId(computed.path, 'projection')}`,
        kind,
        label: `Compute ${computed.path}`,
        required: true,
        inputNodeIds: [primaryGlobalNode.id],
        outputNodeIds: [computed.id],
        effectBoundaryRefs: [],
        compilerTargetAction: 'compute',
        compilerParamBindings: [
          compilerParamBinding('source', 'expression', {
            sourceNodeId: primaryGlobalNode.id,
            expression: expressionForNode(primaryGlobalNode),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: computed.id,
            value: computed.path,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('value', 'expression', {
            sourceNodeId: primaryGlobalNode.id,
            targetNodeId: computed.id,
            expression: expressionForNode(primaryGlobalNode),
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: [
          'cogo:ComputeProjectionPrimitive',
          'cogo:ExpressionPrefix',
        ],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'derive',
        primaryGlobalNode.id,
        computed.id,
        primitive.id,
        primitive.evidenceRefs,
      )
      return
    }

    if (kind === 'navigate') {
      const boundaryRef =
        navigationBoundaryRef ?? getOrCreateBoundary('navigation')
      const route = addNode(
        createNode('route', 'route.path', {
          writable: false,
          evidenceRefs: plannerEvidenceRefs,
          ontologyRefs: ['cogo:RouteContext', 'cogo:NavigationGuard'],
        }),
      )
      const primitive = addPrimitive({
        id: 'primitive:navigate',
        kind,
        label: 'Navigate between application routes',
        required: true,
        inputNodeIds: [route.id],
        outputNodeIds: [route.id],
        effectBoundaryRefs: [boundaryRef],
        compilerTargetAction: 'navigate',
        compilerParamBindings: [
          compilerParamBinding('route', 'to', {
            sourceNodeId: route.id,
            expression: expressionForNode(route),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('payload', 'params', {
            sourceNodeId: route.id,
            expression: '#{route.params}',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: ['cogo:NavigationPrimitive', 'cogo:RouteGraph'],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'read',
        route.id,
        route.id,
        primitive.id,
        primitive.evidenceRefs,
        boundaryRef,
      )
      return
    }

    if (kind === 'guard-by-permission') {
      const boundaryRef = authBoundaryRef ?? getOrCreateBoundary('auth')
      const permissions = addNode(
        createNode('appData', 'appData.permissions', {
          evidenceRefs: plannerEvidenceRefs,
          ontologyRefs: ['cogo:PermissionState', 'cogo:RoleGuard'],
        }),
      )
      const primitive = addPrimitive({
        id: 'primitive:guard-by-permission',
        kind,
        label: 'Guard privileged actions by permission state',
        required: true,
        inputNodeIds: [permissions.id],
        outputNodeIds: [permissions.id],
        effectBoundaryRefs: [boundaryRef],
        compilerTargetStepType: 'conditional',
        compilerParamBindings: [
          compilerParamBinding('condition', 'condition', {
            sourceNodeId: permissions.id,
            expression: expressionForNode(permissions),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('capability', 'requiredCapability', {
            value: 'auth',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: ['cogo:PermissionGuardPrimitive', 'cogo:EffectBoundary'],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'guard',
        permissions.id,
        permissions.id,
        primitive.id,
        primitive.evidenceRefs,
        boundaryRef,
      )
      return
    }

    if (kind === 'trigger-ui-feedback') {
      const boundaryRef = getOrCreateBoundary('ui')
      const sourceNode = computedNodes[0] ?? primaryGlobalNode
      const primitive = addPrimitive({
        id: 'primitive:trigger-ui-feedback',
        kind,
        label: 'Trigger user-visible feedback after an ActionFlow outcome',
        required: true,
        inputNodeIds: [sourceNode.id],
        outputNodeIds: [sourceNode.id],
        effectBoundaryRefs: [boundaryRef],
        compilerTargetAction: 'showToast',
        compilerParamBindings: [
          compilerParamBinding('source', 'messageSource', {
            sourceNodeId: sourceNode.id,
            expression: expressionForNode(sourceNode),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('payload', 'message', {
            sourceNodeId: sourceNode.id,
            value: 'Action completed',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: ['cogo:UiFeedbackPrimitive', 'cogo:EffectBoundary'],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'read',
        sourceNode.id,
        sourceNode.id,
        primitive.id,
        primitive.evidenceRefs,
        boundaryRef,
      )
      return
    }

    if (kind === 'subscribe-realtime-source') {
      const pair = getOrCreateDatasourcePair('realtime', {
        datasourceName: 'realtimeMessages',
      })
      const boundaryRef = getOrCreateBoundary('realtime')
      const primitive = addPrimitive({
        id: `primitive:subscribe-realtime:${stableId(pair.datasourceNode.datasourceName, 'realtime')}`,
        kind,
        label: `Subscribe to ${pair.datasourceNode.datasourceName ?? 'realtime source'} updates`,
        required: true,
        inputNodeIds: [pair.datasourceNode.id],
        outputNodeIds: [pair.cacheNode.id],
        effectBoundaryRefs: [boundaryRef],
        compilerTargetAction: 'restApi',
        compilerParamBindings: [
          compilerParamBinding('source', 'datasourceName', {
            sourceNodeId: pair.datasourceNode.id,
            value: pair.datasourceNode.datasourceName,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('target', 'target', {
            targetNodeId: pair.cacheNode.id,
            value: pair.cacheNode.path,
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('result', 'resultTarget', {
            resultKey: resultKeyForPrimitiveKind(kind, pair.datasourceNode.datasourceName),
            evidenceRefs: plannerEvidenceRefs,
          }),
          compilerParamBinding('adapter-option', 'method', {
            value: 'SUBSCRIBE',
            evidenceRefs: plannerEvidenceRefs,
          }),
        ],
        ontologyRefs: [
          'cogo:RealtimeSubscriptionPrimitive',
          'cogo:EffectBoundary',
        ],
        evidenceRefs: plannerEvidenceRefs,
      })
      addRelation(
        'cache',
        pair.datasourceNode.id,
        pair.cacheNode.id,
        primitive.id,
        primitive.evidenceRefs,
        boundaryRef,
      )
    }
  }

  const builderPrimitiveKinds = new Set<StudioActionFlowPrimitiveKind>(
    plannerPrimitiveKinds,
  )
  if (REALTIME_WORDS.test(appText(appSpec))) {
    builderPrimitiveKinds.add('subscribe-realtime-source')
  }
  if (UI_FEEDBACK_WORDS.test(appText(appSpec))) {
    builderPrimitiveKinds.add('trigger-ui-feedback')
  }

  for (const kind of builderPrimitiveKinds) {
    ensurePlannerPrimitiveKind(kind)
  }

  return {
    schemaVersion: 'studio-actionflow-state-topology.v1',
    source,
    generatedAt: new Date().toISOString(),
    nodes: [...nodes.values()],
    relations: [...relations.values()],
    primitives: [...primitives.values()],
    effectBoundaries: [...boundaries.values()],
  }
}

function requiredPrimitiveKinds(
  appSpec: AppSpecFull,
): StudioActionFlowPrimitiveKind[] {
  const required = new Set<StudioActionFlowPrimitiveKind>(
    actionFlowOntologyPrimitiveKinds(appSpec),
  )
  if (!hasActionFlowNeed(appSpec)) return []
  if (
    appSpec.interactionContract?.requiredStateScopes.includes('local') ||
    appSpec.blocks.some(blockNeedsLocalState)
  ) {
    required.add('set-local-state')
  }
  if (
    appSpec.interactionContract?.requiredStateScopes.includes('local') &&
    appSpec.interactionContract?.requiredStateScopes.includes('appData')
  ) {
    required.add('promote-local-to-global')
  }
  if (normalizeDatasourceSpecs(appSpec).length > 0) {
    required.add('fetch-datasource-to-global')
  }
  if (appSpec.interactionContract?.requireComputedProjection) {
    required.add('compute-projection')
  }
  if (NAVIGATION_WORDS.test(appText(appSpec))) {
    required.add('navigate')
  }
  if (AUTH_WORDS.test(appText(appSpec))) {
    required.add('guard-by-permission')
  }
  if (UI_FEEDBACK_WORDS.test(appText(appSpec))) {
    required.add('trigger-ui-feedback')
  }
  if (REALTIME_WORDS.test(appText(appSpec))) {
    required.add('subscribe-realtime-source')
  }
  if (
    normalizeDatasourceSpecs(appSpec).length > 0 &&
    MUTATION_WORDS.test(appText(appSpec))
  ) {
    required.add('write-global-to-datasource')
  }
  return [...required]
}

export function validateActionFlowTopologyContract(
  topology: StudioActionFlowTopologyContract | null | undefined,
  appSpec: AppSpecFull,
): StudioActionFlowValidationProof {
  const issues: StudioActionFlowValidationIssue[] = []
  const requiredKinds = requiredPrimitiveKinds(appSpec)
  const addIssue = (issue: StudioActionFlowValidationIssue) =>
    issues.push(issue)

  if (!topology) {
    return {
      schemaVersion: 'studio-actionflow-validation-proof.v1',
      valid: requiredKinds.length === 0,
      issues:
        requiredKinds.length === 0
          ? []
          : [
              {
                code: 'missing-state-topology',
                severity: 'error',
                message:
                  'ActionFlow generation requires a state topology contract.',
              },
            ],
      summary: {
        nodes: 0,
        relations: 0,
        primitives: 0,
        effectBoundaries: 0,
        requiredPrimitiveKinds: requiredKinds,
        coveredPrimitiveKinds: [],
        missingPrimitiveKinds: requiredKinds,
      },
    }
  }

  const nodes = new Map<string, StudioActionFlowStateNode>()
  const boundaries = new Map(
    topology.effectBoundaries.map((boundary) => [boundary.id, boundary]),
  )
  const primitiveKinds = new Set(
    topology.primitives.map((primitive) => primitive.kind),
  )

  for (const node of topology.nodes) {
    if (nodes.has(node.id)) {
      addIssue({
        code: 'duplicate-node',
        severity: 'error',
        nodeId: node.id,
        message: `State node "${node.id}" is duplicated.`,
      })
    }
    nodes.set(node.id, node)
    if (node.scope === 'local' && !node.ownerBlockName) {
      addIssue({
        code: 'local-state-missing-owner',
        severity: 'error',
        nodeId: node.id,
        message: `Local state node "${node.id}" must declare ownerBlockName.`,
      })
    }
  }

  for (const primitive of topology.primitives) {
    if (
      primitive.required &&
      (primitive.ontologyRefs.length === 0 ||
        primitive.evidenceRefs.length === 0)
    ) {
      addIssue({
        code: 'primitive-missing-evidence',
        severity: 'error',
        primitiveId: primitive.id,
        message: `Required primitive "${primitive.id}" must preserve ontologyRefs and evidenceRefs.`,
      })
    }
    for (const nodeId of [
      ...primitive.inputNodeIds,
      ...primitive.outputNodeIds,
    ]) {
      if (!nodes.has(nodeId)) {
        addIssue({
          code: 'missing-node',
          severity: 'error',
          primitiveId: primitive.id,
          nodeId,
          message: `Primitive "${primitive.id}" references missing node "${nodeId}".`,
        })
      }
    }
    for (const boundaryRef of primitive.effectBoundaryRefs) {
      if (!boundaries.has(boundaryRef)) {
        addIssue({
          code: 'datasource-effect-missing-boundary',
          severity: 'error',
          primitiveId: primitive.id,
          message: `Primitive "${primitive.id}" references missing effect boundary "${boundaryRef}".`,
        })
      }
    }
  }

  for (const relation of topology.relations) {
    const source = nodes.get(relation.sourceNodeId)
    const target = nodes.get(relation.targetNodeId)
    if (!source || !target) {
      addIssue({
        code: 'relation-missing-node',
        severity: 'error',
        relationId: relation.id,
        message: `Relation "${relation.id}" references a missing source or target node.`,
      })
      continue
    }
    if (
      source.scope === 'local' &&
      target.scope === 'local' &&
      source.ownerBlockName !== target.ownerBlockName &&
      (relation.kind === 'write' ||
        relation.kind === 'promote' ||
        relation.kind === 'sync')
    ) {
      addIssue({
        code: 'cross-block-local-write',
        severity: 'error',
        relationId: relation.id,
        message: `Relation "${relation.id}" writes across local state owners.`,
      })
    }
    if (
      target.scope === 'computed' &&
      relation.kind !== 'derive' &&
      relation.kind !== 'read'
    ) {
      addIssue({
        code: 'computed-direct-write',
        severity: 'error',
        relationId: relation.id,
        message: `Computed node "${target.id}" can only be targeted by derive relations.`,
      })
    }
    if (
      target.scope === 'appData' &&
      (relation.kind === 'promote' ||
        relation.kind === 'sync' ||
        relation.kind === 'cache') &&
      !relation.effectBoundaryRef
    ) {
      addIssue({
        code: 'global-write-missing-boundary',
        severity: 'error',
        relationId: relation.id,
        message: `Global write relation "${relation.id}" must declare an effect boundary.`,
      })
    }
    if (
      (source.scope === 'datasource' || target.scope === 'datasource') &&
      (relation.kind === 'cache' || relation.kind === 'sync') &&
      (!relation.effectBoundaryRef ||
        !boundaries.get(relation.effectBoundaryRef)?.sideEffect)
    ) {
      addIssue({
        code: 'datasource-effect-missing-boundary',
        severity: 'error',
        relationId: relation.id,
        message: `Datasource relation "${relation.id}" must cross a side-effect boundary.`,
      })
    }
  }

  const missingPrimitiveKinds = requiredKinds.filter(
    (kind) => !primitiveKinds.has(kind),
  )
  for (const kind of missingPrimitiveKinds) {
    addIssue({
      code: 'missing-primitive',
      severity: 'error',
      message: `Required ActionFlow primitive "${kind}" is missing from the topology contract.`,
    })
  }

  const headlessProof = validateHeadlessActionFlowTopologyContract(topology)
  if (!headlessProof.ok) {
    for (const error of headlessProof.errors) {
      addIssue({
        code: 'actionflow-ontology-contract-invalid',
        severity: 'error',
        message: `ActionFlow ontology contract violation: ${error}`,
      })
    }
  }

  return {
    schemaVersion: 'studio-actionflow-validation-proof.v1',
    valid: !issues.some((issue) => issue.severity === 'error'),
    issues,
    summary: {
      nodes: topology.nodes.length,
      relations: topology.relations.length,
      primitives: topology.primitives.length,
      effectBoundaries: topology.effectBoundaries.length,
      requiredPrimitiveKinds: requiredKinds,
      coveredPrimitiveKinds: [...primitiveKinds],
      missingPrimitiveKinds,
    },
  }
}

export function withActionFlowTopologyContract(
  appSpec: AppSpecFull,
  options: BuildActionFlowTopologyOptions = {},
): AppSpecFull {
  const topology =
    appSpec.actionFlowTopology ??
    buildActionFlowTopologyContract(appSpec, options)
  return {
    ...appSpec,
    actionFlowTopology: topology,
    actionFlowValidationProof: validateActionFlowTopologyContract(
      topology,
      appSpec,
    ),
  }
}
